import React, { useRef } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Button,
  ButtonGroup,
  VStack,
  FormControl,
  FormLabel,
  Alert,
  AlertDescription,
  AlertIcon,
  useToast,
} from '@chakra-ui/react';
import {
  TeamNameField,
  TeamDescriptionField,
  OrganizationAccessField,
  AppAccessField,
} from './components';
import { CreateTeamDrawerProps } from './types';
import { useCreateTeamForm } from './hooks';
import { AddMembersDropdown } from '../AddMembersDropdown/AddMembersDropdown';
import { useDrawerStateDispatch } from 'features/main/context';
import {
  SELECT_AT_LEAST_ONE_APPLICATION_MSG,
  USER_GROUPS_PERMISSIONS_INFO_MSG,
} from './utils';

export const CreateTeamDrawer: React.FC<CreateTeamDrawerProps> = () => {
  const initialFocusRef = useRef<HTMLInputElement>(null);
  const [{ drawerVisible, drawerId }] = useDrawerStateDispatch();

  // Use the custom hook that encapsulates all form logic
  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    handleBlur,
    isSubmitting,
    productAccessState,
    setProductAccessState,
    selectedMembers,
    handleMembersSelect,
    handleCloseDrawer,
  } = useCreateTeamForm();

  const toast = useToast();

  const isOpen = drawerVisible && drawerId === 'create-team-drawer';

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (productAccessState.length === 0) {
      event.preventDefault();
      toast({
        status: 'error',
        description: SELECT_AT_LEAST_ONE_APPLICATION_MSG,
        isClosable: true,
      });
      return;
    }
    handleSubmit(event);
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleCloseDrawer}
      initialFocusRef={initialFocusRef}
      size='lg'
    >
      <DrawerOverlay />
      <form onSubmit={handleFormSubmit} data-testid='create-team-form'>
        <DrawerContent data-testid='create-team-drawer'>
          <DrawerHeader display='flex' alignItems='center'>
            Create group
            <DrawerCloseButton />
          </DrawerHeader>

          <DrawerBody>
            <VStack spacing={6} align='stretch'>
              <TeamNameField
                name='name'
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                handleBlur={handleBlur}
                initialFocusRef={initialFocusRef}
              />

              <TeamDescriptionField
                name='description'
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                handleBlur={handleBlur}
              />

              <VStack spacing={2} align='stretch'>
                <Alert
                  status='info'
                  data-testid='user-groups-permissions-info-alert'
                >
                  <AlertIcon />
                  <AlertDescription>
                    {USER_GROUPS_PERMISSIONS_INFO_MSG}
                  </AlertDescription>
                </Alert>
                <FormControl data-testid='create-team-members-field'>
                  <FormLabel htmlFor='team-members'>Users</FormLabel>
                  <AddMembersDropdown
                    onMembersSelect={handleMembersSelect}
                    value={selectedMembers}
                  />
                </FormControl>
              </VStack>

              <OrganizationAccessField />

              <AppAccessField
                productAccessState={productAccessState}
                setProductAccessState={setProductAccessState}
                showAppAccessError={
                  Object.keys(touched).length > 0 &&
                  productAccessState.length === 0
                }
              />
            </VStack>
          </DrawerBody>

          <DrawerFooter justifyContent='flex-end'>
            <ButtonGroup>
              <Button
                variant='ghost'
                onClick={handleCloseDrawer}
                isDisabled={isSubmitting}
                data-testid='create-team-cancel-button'
              >
                Cancel
              </Button>
              <Button
                type='submit'
                variant='solid'
                isLoading={isSubmitting}
                isDisabled={
                  !values.name.trim() ||
                  !!errors.name ||
                  productAccessState.length === 0
                }
                data-testid='create-team-submit-button'
              >
                Create group
              </Button>
            </ButtonGroup>
          </DrawerFooter>
        </DrawerContent>
      </form>
    </Drawer>
  );
};
